import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { LiveContextDto } from '@/types/dtos/live-contexts/live-context.dto.ts';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow.ts';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { getObjectId } from '@/helpers/object.id.helper';

export type ToggleLiveContextMutationVariables = {
  entityType: LiveContextDto['entityType'];
  entityId: string;
  /** Passed when starting live in a slot-scoped flow; backend decides scoped vs master PLAY bootstrap. */
  eventSlotId?: string;
  /** Targeted refetch after toggle (e.g. `[GET_FLOW_QUERY_KEY, originId, eventSlotId]`). */
  flowQueryKey?: QueryKey;
};

type ToggleLiveContextResponse = {
  message: string;
  liveContextId: string;
  entityType: LiveContextDto['entityType'];
  entityId: string;
  isRunning: boolean;
  isNewLiveContext: boolean;
};

const updateFlowLiveContext = (flow: FlowDto | undefined, response: ToggleLiveContextResponse) => {
  if (!flow) return flow;
  const bindingId = flow.liveBindingFlowId ?? (flow.origin ? getObjectId(flow.origin) : flow.id);
  if (bindingId !== response.entityId && flow.id !== response.entityId) return flow;

  const nextLiveContext: LiveContextDto = flow.liveContext
    ? {
        ...flow.liveContext,
        entityType: response.entityType,
        entityId: response.entityId,
        isRunning: response.isRunning,
        updatedAt: new Date(),
      }
    : {
        id: response.liveContextId,
        entityType: response.entityType,
        entityId: response.entityId,
        isRunning: response.isRunning,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

  return { ...flow, liveContext: nextLiveContext };
};

export const useToggleLiveContextMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ entityType, entityId, eventSlotId }: ToggleLiveContextMutationVariables) => {
      const token = await getToken();
      return toggleLiveContext({ entityType, entityId, eventSlotId }, token ?? '');
    },
    onSuccess: (data, variables) => {
      const flowStore = useFlowStore.getState();
      const nextFlow = updateFlowLiveContext(flowStore.flow, data);
      const nextFlowPlan = updateFlowLiveContext(flowStore.flowPlan, data);
      const nextFlowOrganize = updateFlowLiveContext(flowStore.flowOrganize, data);

      if (nextFlow && nextFlow !== flowStore.flow) {
        flowStore.setFlow(nextFlow);
      }
      if (nextFlowPlan && nextFlowPlan !== flowStore.flowPlan) {
        flowStore.setFlowPlan(nextFlowPlan);
      }
      if (nextFlowOrganize && nextFlowOrganize !== flowStore.flowOrganize) {
        flowStore.setFlowOrganize(nextFlowOrganize);
      }

      queryClient.setQueriesData({ queryKey: [GET_FLOW_QUERY_KEY] }, (cached) => {
        if (!cached) return cached;
        if (Array.isArray(cached)) {
          return cached.map((flow) => updateFlowLiveContext(flow, data));
        }
        return updateFlowLiveContext(cached as FlowDto, data);
      });

      if (variables.flowQueryKey != null && variables.flowQueryKey.length >= 2) {
        void queryClient.refetchQueries({ queryKey: variables.flowQueryKey });
      } else {
        void queryClient.refetchQueries({ queryKey: [GET_FLOW_QUERY_KEY, variables.entityId] });
      }
    },
  });
};

type ToggleLiveContextRequest = Pick<ToggleLiveContextMutationVariables, 'entityType' | 'entityId' | 'eventSlotId'>;

const toggleLiveContext = async (body: ToggleLiveContextRequest, token: string): Promise<ToggleLiveContextResponse> => {
  const url = `${import.meta.env.VITE_API_URL}/player/live-context`;
  return sendJSONRequest({ url, method: METHOD.POST, body, token });
};
